import type { ProviderResult } from '@/lib/pricing'
import { fmtUSD } from '@/lib/pricing'

interface Props {
  results: ProviderResult[]
}

const CATEGORIES = [
  { key: 'compute' as const, label: 'Compute', color: '#38E1FF' },
  { key: 'storage' as const, label: 'Storage', color: '#34D399' },
  { key: 'network' as const, label: 'Network', color: '#FBBF24' },
  { key: 'serverless' as const, label: 'Serverless', color: '#A78BFA' },
]

function CostBreakdownChart({ results }: Props) {
  const max = Math.max(...results.map(r => r.total), 1)

  return (
    <div className="rounded-2xl border border-[#1F2A44] bg-[#0E1525]/80 backdrop-blur-sm p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-200 tracking-tight">Cost Allocation</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Stacked breakdown · monthly</p>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1.5">
          {CATEGORIES.map(c => (
            <div key={c.key} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: c.color }}></span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{c.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {results.map(r => (
          <div key={r.provider.id}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.provider.color }} />
                <span className="text-[12px] font-semibold text-slate-300">{r.provider.shortName}</span>
              </div>
              <span className="font-mono text-[12px] text-slate-400 tabular-nums">{fmtUSD(r.total)}</span>
            </div>
            <div className="flex h-7 rounded-md overflow-hidden bg-[#131B30] border border-[#1F2A44]/60" style={{ width: `${(r.total / max) * 100}%`, minWidth: '20%' }}>
              {CATEGORIES.map(c => {
                const v = r[c.key]
                const pct = (v / r.total) * 100
                if (pct < 0.5) return null
                return (
                  <div
                    key={c.key}
                    className="relative group/seg flex items-center justify-center transition-all"
                    style={{ width: `${pct}%`, background: c.color, opacity: 0.85 }}
                    title={`${c.label}: ${fmtUSD(v)} (${pct.toFixed(1)}%)`}
                  >
                    {pct > 14 && (
                      <span className="font-mono text-[10px] text-[#0B0E1A] font-bold tabular-nums">
                        {pct.toFixed(0)}%
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CostBreakdownChart
